/**
 * MobileLootSidebar — full-screen modal for LabyrinthState portrait mode
 * showing weapons / equipment / loot-bag cells. Tap a cell for its tooltip,
 * tap outside or the close button to dismiss.
 */
import { Container, Graphics, Text } from 'pixi.js';
import {
  MARGIN, PAD_M, GAP,
  ACCENT, ACCENT_DIM, PANEL_FILL,
  FONT_HEADER, FONT_SUBHEADER, TEXT_PRIMARY, TEXT_DIM, GUGI_SHADOW,
  drawStandardPanel,
} from './LayoutConstants';
import { drawHeaderBand } from './PanelUtils';
import { attachScrollDrag } from './ScrollablePanel';
import { createScrollBar, SCROLLBAR_WIDTH } from './ScrollBar';
import type { UpgradeCategory, UpgradeDef } from '../upgrades/UpgradeData';
import type { WeaponDef } from '../upgrades/WeaponData';
import type { ScoredItem } from '../upgrades/Quality';
import {
  buildUpgradeCell, buildWeaponCell,
  createUpgradeTooltip, createWeaponTooltip, positionTooltip,
} from './InventoryUI';

interface EquippedWeaponSlot { def: WeaponDef; item: ScoredItem }

export interface MobileLootSnapshot {
  weapons: (EquippedWeaponSlot | null)[];
  equipment: { cat: UpgradeCategory; def: UpgradeDef; item: ScoredItem }[];
  loot: { def: UpgradeDef | WeaponDef; item: ScoredItem }[];
  coinsThisRun: number;
}

export interface MobileLootSidebarOptions {
  vpW: number;
  vpH: number;
  safeTop?: number;
  safeBottom?: number;
  snapshot: MobileLootSnapshot;
  onClose: () => void;
}

export class MobileLootSidebar extends Container {
  private tooltip: Container | null = null;

  constructor(opts: MobileLootSidebarOptions) {
    super();
    const { vpW, vpH, snapshot, onClose } = opts;
    const safeTop = opts.safeTop ?? 0;
    const safeBottom = opts.safeBottom ?? 0;

    // Backdrop
    const backdrop = new Graphics();
    backdrop.rect(0, 0, vpW, vpH);
    backdrop.fill({ color: 0x000000, alpha: 0.75 });
    backdrop.eventMode = 'static';
    backdrop.on('pointerdown', () => onClose());
    this.addChild(backdrop);

    const cardX = MARGIN;
    const cardY = safeTop + MARGIN;
    const cardW = vpW - MARGIN * 2;
    const cardH = vpH - cardY - safeBottom - MARGIN;
    const card = new Container();
    card.position.set(cardX, cardY);
    card.eventMode = 'static';
    card.on('pointerdown', (e) => e.stopPropagation());
    this.addChild(card);

    const bg = new Graphics();
    drawStandardPanel(bg, 0, 0, cardW, cardH);
    card.addChild(bg);

    const titleText = new Text({
      text: 'GEAR',
      style: { ...FONT_HEADER, fill: ACCENT, dropShadow: GUGI_SHADOW },
    });
    titleText.position.set(PAD_M, PAD_M);
    card.addChild(titleText);

    const credits = new Text({
      text: `Cr  ${snapshot.coinsThisRun}`,
      style: { ...FONT_SUBHEADER, fill: 0xffdd44 },
    });
    credits.anchor.set(1, 0);
    credits.position.set(cardW - PAD_M - 40, PAD_M + 3);
    card.addChild(credits);

    // Close button — 44x44 WCAG tap target
    const CLOSE_SZ = 44;
    const closeBtn = new Container();
    closeBtn.position.set(cardW - CLOSE_SZ - 4, 4);
    const closeBg = new Graphics();
    closeBg.roundRect(0, 0, CLOSE_SZ, CLOSE_SZ, 6);
    closeBg.fill({ color: 0x000000, alpha: 0.4 });
    closeBg.stroke({ color: ACCENT_DIM, width: 1 });
    closeBtn.addChild(closeBg);
    const closeX = new Text({
      text: '\u00D7',
      style: { ...FONT_HEADER, fontSize: 28, fill: TEXT_PRIMARY },
    });
    closeX.anchor.set(0.5);
    closeX.position.set(CLOSE_SZ / 2, CLOSE_SZ / 2);
    closeBtn.addChild(closeX);
    closeBtn.eventMode = 'static';
    closeBtn.cursor = 'pointer';
    closeBtn.on('pointerdown', (e) => { e.stopPropagation(); onClose(); });
    card.addChild(closeBtn);

    // Scrollable content region — title/credits/close stay on `card`, cells scroll.
    const scrollTopY = 40;
    const scrollH = cardH - scrollTopY - PAD_M;
    const scrollContent = new Container();
    scrollContent.eventMode = 'static';
    card.addChild(scrollContent);

    const scrollMask = new Graphics();
    scrollMask.rect(0, scrollTopY, cardW, scrollH);
    scrollMask.fill(0xffffff);
    card.addChild(scrollMask);
    scrollContent.mask = scrollMask;

    const scrollHit = new Graphics();
    scrollHit.eventMode = 'static';
    scrollContent.addChild(scrollHit);

    // Cell grid — dynamic column count to fit cardW
    const cellSize = 48;
    const cellGap = 6;
    const cols = Math.max(3, Math.floor((cardW - PAD_M * 2 + cellGap) / (cellSize + cellGap)));
    const gridX = PAD_M;
    let py = scrollTopY;

    let scrollDragHandle: ReturnType<typeof attachScrollDrag> | null = null;

    const drawHeader = (label: string) => {
      const band = new Graphics();
      drawHeaderBand(band, PAD_M - 4, py - 2, cardW - PAD_M * 2 + 8, 0x1a0a0d, PANEL_FILL, 22);
      scrollContent.addChild(band);
      scrollContent.addChild(new Text({
        text: label,
        style: { ...FONT_SUBHEADER, fill: TEXT_DIM, dropShadow: GUGI_SHADOW },
      })).position.set(PAD_M, py);
      py += 22;
    };

    const addCellWithTooltip = (
      cell: Container, cx: number, cy: number,
      tipFactory: () => { tip: Container; width: number; height: number },
    ) => {
      cell.position.set(cx, cy);
      cell.eventMode = 'static';
      cell.cursor = 'pointer';
      cell.on('pointertap', (e) => {
        e.stopPropagation();
        if (scrollDragHandle?.wasDragging()) return;
        this.hideTooltip();
        const { tip, width, height } = tipFactory();
        positionTooltip(tip, height, width, cardX + cx, cardY + cy, vpW, vpH, cellSize);
        tip.position.x -= cardX;
        tip.position.y -= cardY;
        card.addChild(tip);
        this.tooltip = tip;
      });
      scrollContent.addChild(cell);
    };

    const layoutGrid = (items: { cell: Container; tip: () => { tip: Container; width: number; height: number } }[]) => {
      let col = 0;
      for (const it of items) {
        const cx = gridX + col * (cellSize + cellGap);
        addCellWithTooltip(it.cell, cx, py, it.tip);
        col++;
        if (col >= cols) { col = 0; py += cellSize + cellGap; }
      }
      if (col > 0) py += cellSize + cellGap;
      py += GAP;
    };

    // Weapons
    const weaponCells = snapshot.weapons
      .filter((w): w is EquippedWeaponSlot => w !== null)
      .map(w => ({
        cell: buildWeaponCell(w.def, w.item, cellSize),
        tip: () => createWeaponTooltip(w.def, w.item),
      }));
    if (weaponCells.length > 0) { drawHeader('WEAPONS'); layoutGrid(weaponCells); }

    // Equipment
    const equipCells = snapshot.equipment.map(e => ({
      cell: buildUpgradeCell(e.def, e.item, cellSize),
      tip: () => createUpgradeTooltip(e.def, e.item),
    }));
    if (equipCells.length > 0) { drawHeader('EQUIPMENT'); layoutGrid(equipCells); }

    // Loot bag
    if (snapshot.loot.length > 0) {
      drawHeader(`LOOT BAG (${snapshot.loot.length})`);
      const lootCells = snapshot.loot.map(l => {
        const isWeapon = l.def.kind === 'weapon';
        return isWeapon
          ? {
              cell: buildWeaponCell(l.def as WeaponDef, l.item, cellSize),
              tip: () => createWeaponTooltip(l.def as WeaponDef, l.item),
            }
          : {
              cell: buildUpgradeCell(l.def as UpgradeDef, l.item, cellSize),
              tip: () => createUpgradeTooltip(l.def as UpgradeDef, l.item),
            };
      });
      layoutGrid(lootCells);
    }

    // Set up scroll + wheel + drag if content overflows the visible area.
    const totalH = py;
    const maxScroll = Math.max(0, totalH - (scrollTopY + scrollH));
    scrollHit.clear();
    scrollHit.rect(0, scrollTopY, cardW, Math.max(scrollH, totalH - scrollTopY));
    scrollHit.fill({ color: 0x000000, alpha: 0.001 });

    let scrollY = 0;
    let scrollBar: ReturnType<typeof createScrollBar> | null = null;
    if (maxScroll > 0) {
      scrollBar = createScrollBar({
        trackHeight: scrollH,
        contentHeight: totalH - scrollTopY,
        visibleHeight: scrollH,
        onChange: (frac) => {
          scrollY = frac * maxScroll;
          scrollContent.y = -scrollY;
        },
      });
      scrollBar.container.position.set(cardW - SCROLLBAR_WIDTH - 2, scrollTopY);
      card.addChild(scrollBar.container);
    }

    scrollDragHandle = attachScrollDrag({
      hitArea: scrollContent,
      onDelta: (dy: number) => {
        if (maxScroll <= 0) return;
        scrollY = Math.max(0, Math.min(maxScroll, scrollY + dy));
        scrollContent.y = -scrollY;
        scrollBar?.setFraction(scrollY / maxScroll);
      },
    });
  }

  private hideTooltip() {
    if (this.tooltip) {
      this.tooltip.parent?.removeChild(this.tooltip);
      this.tooltip.destroy({ children: true });
      this.tooltip = null;
    }
  }
}
